import { useState, useEffect, useRef } from "react";
import {
  Button,
  Typography,
} from "@material-ui/core";
import { format, parseISO } from "date-fns";
import axios from "axios";

import { Loader, useAuth, useQuery, useToast, useMutation } from "@webapps/reactjs-tech-components";
import { getAbsenceByStudentId, getAbsenceHoursByStudentIdAndClassroomId, getSchoolNameByClassroomId, downloadPlanning } from "@webapps/business";
import PlanningToExport from "./PlanningToExport";
import { ChevronLeft, ChevronRight, GetApp, Refresh, Replay, Send } from "@material-ui/icons";
import { ROUTES_PATH } from "config/constants";
import { useHistory } from "react-router-dom";
import { useStyles } from "@webapps/styles";
import CalendarContainer from "./CalendarContainer";
import { useCalendar } from "../Stores/CalendarProvider";
import planningColors, { planningColorsText } from "../../Classes/colors/planningColors";
import PlanningManagerMiddle from "./PlanningManagerMiddle";
import Modal from "components/Modal";

const { NX_REST_API } = process.env;

function CircleWithLabel({ color, label }) {
  const classes = useStyles();
  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      <div className={classes.circleBorder} style={{ borderColor: color }}>
        <div className={classes.circle} style={{ background: color }} />
      </div>

      <span className={classes.circleLabel}>{label}</span>
    </div>
  );
}

/**
 * PlanningManager component to manage the planning of classes.
 * 
 * @param {Object} props - The component props.
 * @param {string} props.studentId - L'id de l'étudiant.
 * @param {string} props.classroomId - L'id de la classe.
 * @param {string} props.from - La source de la page. (Ex: "student", "school", "business")
 * @param {boolean} props.isClassAuthorized - Si l'utilisateur est autorisé à modifier le planning.
 * @param {Array} props.students - La liste des étudiants.
 * @returns {JSX.Element} The rendered component.
 */
const PlanningManager = ({
  studentId, students = [], classroomId, from,
  isClassAuthorized = false, teamId,
  classroomName, trainingName, trainingDegree,
  classe,
  setOpenDeletePeriods,
  onDeletePeriods,
  setOnDeletePeriods,
  handleGeneratePDFWithNode,
  isDownloadingPlanning,
  setIsDownloadingPlanning,
  displayPlanningToExport,
  setDisplayPlanningToExport
}) => {
  const {
    classes,
    css,
    cx,
    global,
    theme: { spacing },
  } = useStyles(styles);

  if (!classroomId) {
    return <Loader />
  }

  const [isLoading, setIsLoading] = useState(true);
  const [planning, setPlanning] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [newPeriod, setNewPeriod] = useState({
    startDate: "",
    endDate: "",
    type: "FORMATION",
  });
  const [holidays, setHolidays] = useState([]); // État pour les jours fériés
  const [startDate, setStartDate] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [periodModalData, setPeriodModalData] = useState({});
  const [selectedPeriodToDelete, setSelectedPeriodToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalPeriodType, setModalPeriodType] = useState("FORMATION");
  const [endDate, setEndDate] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const contentRef = useRef();
  const hiddenRef = useRef();
  const history = useHistory();

  const { state, dispatch } = useCalendar();
  const { addToast } = useToast();

  // Autorisation de modification du planning si l'utilisateur est la classe detentrice du planning
  // const [isClassAuthorized, setIsClassAuthorized] = useState(true);
  const { idUser } = useAuth();

  const { data: school = {} } = useQuery(getSchoolNameByClassroomId, {
    key: ['schoolByClassroomId', classroomId + idUser],
    variables: { id: classroomId },
    enabled: !!classroomId,
  });

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchPlanning(),
        getClassDates(),
        fetchHolidays(),
        from === "student" ? refetchAbsences() : Promise.resolve()
      ]);
      setIsLoading(false);
    };

    fetchAllData();
  }, [from]);

  const handlePdfReady = () => {
    setPdfReady(true);
  };

  useEffect(() => {
    if (displayPlanningToExport && hiddenRef.current && pdfReady) {
      // Le composant PlanningToExport est monté et le ref est dispo
      generatePDFWithNode();
    }
  }, [displayPlanningToExport, pdfReady]);

  const {
    error: errorDownloadPlanning,
    isLoading: isLoadingDownloadPlanning,
    isSuccess: isSuccessDownloadPlanning,
    data: dataObject,
    mutateAsync: mutateDownloadPlanning,
  } = useMutation(downloadPlanning);

  const generatePDFWithNode = async () => {
    if (!hiddenRef.current) return;

    const htmlContent = hiddenRef.current.outerHTML;

    await mutateDownloadPlanning({
      data: { html: htmlContent },
    });
  };

  useEffect(() => {
    if (isSuccessDownloadPlanning && dataObject) {
      const pdfBlob = new Blob([dataObject], { type: "application/pdf" });
      const pdfUrl = window.URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "planning.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(pdfUrl);
      setIsDownloadingPlanning(false);
      setDisplayPlanningToExport(false);
      setPdfReady(false);
    } else if (errorDownloadPlanning) {
      console.error("Error downloading PDF:", errorDownloadPlanning);
      setIsDownloadingPlanning(false);
      setDisplayPlanningToExport(false);
      setPdfReady(false);
    }
  }, [isSuccessDownloadPlanning, errorDownloadPlanning]);

  const handleSetCategoryTotals = (data) => {
    dispatch({ type: "SET_CATEGORIES_TOTALS", payload: data });
  };

  useEffect(() => {
    calculateCategoryTotals();
  }, [periods, startDate, endDate]);

  const handleOpenModal = (data) => {
    if (from === "student") return;
    if (!state.hoursPerDay) {
      addToast({
        status: 'error',
        message: `Veuillez entrer une valeur pour les heures par jour.`,
      });
      dispatch({ type: "RESET_SELECTION" });
      return;
    }
    setModalOpen(true);

    setPeriodModalData(data)
  };

  const handleOpenDeleteModal = (period, { startDate, endDate }) => {
    if (from === "student") return;
    setDeleteModalOpen(true);

    setSelectedPeriodToDelete({
      ...period,
      startDate,
      endDate
    });
  };

  const handleAddPeriodFromModal = async () => {
    if (from === "student") return;
    if (!state.selectedStartDay || !state.selectedEndDay) return;

    const newPeriod = {
      startDate: format(state.selectedStartDay, "yyyy-MM-dd"),
      endDate: format(state.selectedEndDay, "yyyy-MM-dd"),
      type: modalPeriodType,
      hoursPerDay: state.hoursPerDay,
    };

    try {
      const response = await axios.post(
        `${NX_REST_API}/schools/module/planning/classroom/${classroomId}/add-period`,
        newPeriod
      );

      const addedPeriods = response.data;

      if (!planning?.id) {
        fetchPlanning();
      }

      if (Array.isArray(addedPeriods)) {
        setPeriods((prev) => [...prev, ...addedPeriods]); // Ajoute tous les éléments du tableau
      } else {
        console.error("Unexpected response format: ", addedPeriods);
      }

      setPeriodModalData({})
      dispatch({ type: "RESET_SELECTION" });
      setModalOpen(false); // Ferme la modale
    } catch (error) {
      console.error("Error adding period:", error);

      addToast({
        status: 'error',
        message: `${error.response?.data?.error || "Une erreur est survenue lors de l'ajout de la période."}`,
      });
      setModalOpen(false);
      dispatch({ type: "RESET_SELECTION" });
    }
  };

  const {
    data: absences = [],
    refetch: refetchAbsences,
    isLoading: isLoadingAbsences,
  } = useQuery(getAbsenceByStudentId, {
    key: ['absencesPlanning', idUser],
    variables: { id: idUser },
    enabled: false,
  })

  const {
    data: absenceHours = null,
    refetch: refetchAbsenceHours,
    isLoading: isLoadingAbsenceHours,
  } = useQuery(getAbsenceHoursByStudentIdAndClassroomId, {
    key: ['absenceHoursBusiness', idUser],
    variables: { studentId: idUser, classId: classroomId },
  });

  const fetchHolidays = async () => {
    try {
      const response = await axios.get(`${NX_REST_API}/schools/module/planning/holiday`);

      setHolidays(response.data); // Stocke les jours fériés dans l'état
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  const fetchPlanning = async () => {
    try {
      const response = await axios.get(
        `${NX_REST_API}/schools/module/classrooms/${classroomId}/planning`
      );

      setPlanning(response.data);

      setPeriods(response.data.periods || []);
    } catch (error) {
      console.error("Error fetching planning:", error);
    }
  };

  const getClassDates = async () => {
    try {
      const response = await axios.get(
        `${NX_REST_API}/schools/module/classrooms/${classroomId}/dates`
      );
      setStartDate(response.data.startDate);
      setEndDate(response.data.endDate);
    } catch (error) {
      console.error("Error fetching class dates:", error);
    }
  };

  const normalizeDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const getDaysBetweenDates = () => {
    if (!startDate || !endDate) return [];
    const days = [];
    let currentDate = normalizeDate(startDate);
    const end = normalizeDate(endDate);

    while (currentDate <= end) { // Inclure explicitement la date de fin
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  };

  const getPeriodTypeForDay = (day) => {
    const normalizedDay = normalizeDate(day);
    const period = periods.find((p) => {
      const start = normalizeDate(parseISO(p.startDate));
      const end = normalizeDate(parseISO(p.endDate)); // Pas besoin d'ajouter 1 jour ici
      return normalizedDay >= start && normalizedDay <= end; // Inclure la date de fin
    });
    return period ? period : null;
  };

  const calculateCategoryTotals = () => {
    const totals = {
      FORMATION: { days: 0, hours: 0, visible: true },
      ENTREPRISE: { days: 0, hours: 0, visible: true },
      VACANCES: { days: 0, hours: 0, visible: true },
      FERIES: { days: 0, hours: 0, visible: true }, // Ajout pour les jours fériés
    };

    getDaysBetweenDates().forEach((day) => {
      const period = getPeriodTypeForDay(day);

      const periodType = period ? period.type : null;
      const isWeekend = day.getDay() === 6 || day.getDay() === 0; // 6 = samedi, 0 = dimanche
      const isHolidayDay = isHoliday(day);

      if (isHolidayDay) {
        totals.FERIES.days += 1;
        totals.FERIES.hours += 7;
      }

      if ((periodType === "FORMATION" || periodType === "ENTREPRISE") && !isWeekend && !isHolidayDay) {
        totals[periodType].days += 1;
        totals[periodType].hours += period.hoursPerDay; // Chaque jour compte pour 7 heures
      }

      if (periodType === "VACANCES") {
        totals.VACANCES.days += 1;
        totals.VACANCES.hours += 7;
      }
    });

    handleSetCategoryTotals(totals);
  };

  const handleDeletePeriod = async (periodId) => {
    if (from === "student") return;

    try {
      await axios.delete(`${NX_REST_API}/schools/module/planning/period/${periodId}`);
      setPeriods((prev) => prev.filter((period) => period.id !== periodId));
      setDeleteModalOpen(false);
      setSelectedPeriodToDelete(null);
    } catch (error) {
      console.error("Error deleting period:", error);
    }
  };

  // Supprime toutes les périodes
  const handleDeleteAllPeriods = async () => {
    if (from === "student") return;

    try {
      await axios.delete(`${NX_REST_API}/schools/module/planning/${planning?.id}`);
      setPeriods([]);
      setPlanning(null);
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting all periods:", error);
    }
  };

  useEffect(() => {
    if (onDeletePeriods) {
      handleDeleteAllPeriods();
      setOnDeletePeriods(false);
      setOpenDeletePeriods(false);
    }
  }, [onDeletePeriods]);

  const handleCloseModal = () => {
    setModalOpen(false);
    dispatch({ type: "RESET_SELECTION" });
  };

  const isHoliday = (day) => {
    if (!holidays || holidays.length === 0) return false; // Vérifie si holidays est vide
    const normalizedDay = normalizeDate(day);

    return holidays.some((holiday) => {
      const holidayDate = normalizeDate(new Date(holiday.date)); // Crée un objet Date à partir de holiday.date
      return holidayDate.getTime() === normalizedDay.getTime();
    });
  };

  function getMonthsBetweenDates(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months = [];

    // Create a date for the first day of the start month
    const currentMonth = new Date(start.getFullYear(), start.getMonth(), 1);

    // Create a date for the first day of the month after the end date
    const endMonth = new Date(end.getFullYear(), end.getMonth() + 1, 1);

    // Loop through each month until we reach or pass the end month
    while (currentMonth < endMonth) {
      months.push(new Date(currentMonth)); // Clone the date
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    return months;
  }

  const months = startDate && endDate ? getMonthsBetweenDates(startDate, endDate) : [];

  const goToPreviousPage = () => {
    history.push(ROUTES_PATH.SCHOOL_CLASSES);
  };

  return (
    <div className={classes?.leadContainer}>
      <div className={classes.containerButtonAndPlanning}>
        <div className={classes?.buttonContainer}>
          {!(from === "student") && <PlanningManagerMiddle
            from={from}
            isDownloadingPlanning={isDownloadingPlanning}
            setOpenDeletePeriods={setOpenDeletePeriods}
            handleGeneratePDFWithNode={handleGeneratePDFWithNode}
          />}
        </div>

        <div className={classes.container}>
          <div className={classes.container2}>
            {from === "school" && <>
            </>}
            {displayPlanningToExport && (
              <PlanningToExport
                ref={hiddenRef}
                months={months}
                periods={periods}
                holidays={holidays}
                classroomName={classroomName}
                trainingName={trainingName}
                trainingDegree={trainingDegree}
                startDate={startDate}
                endDate={endDate}
                school={school}
                handlePdfReady={handlePdfReady}
              />
            )}

            <div>
              <CalendarContainer
                months={months}
                absences={absences}
                startDate={startDate}
                endDate={endDate}
                isHoliday={isHoliday}
                getPeriodTypeForDay={getPeriodTypeForDay}
                handleOpenModal={handleOpenModal}
                setPeriodModalData={setPeriodModalData}
                isClassAuthorized={isClassAuthorized}
                periods={periods}
                handleOpenDeleteModal={handleOpenDeleteModal}
                holidays={holidays}
                from={from}
                isLoading={isLoading}
              />
            </div>

            {from === "school" && <Modal open={modalOpen} onCancel={() => handleCloseModal()}
              data={periodModalData}
              withStep={false}
              title="Ajouter une période"
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  {[
                    { value: "FORMATION", label: "Formation", color: planningColorsText.FORMATION },
                    { value: "ENTREPRISE", label: "Entreprise", color: planningColorsText.ENTREPRISE },
                    { value: "VACANCES", label: "Vacances", color: planningColorsText.VACANCES },
                  ].map(({ value, label, color }) => (
                    <div
                      key={value}
                      style={{ display: "flex", alignItems: "center", gap: "20px" }}
                    >
                      <input
                        type="radio"
                        id={value}
                        name="periodType"
                        value={value}
                        checked={modalPeriodType === value}
                        onChange={(e) => {
                          setModalPeriodType(value);
                        }}
                        style={{
                          appearance: "none",
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          border: `2px solid ${color}`,
                          display: "grid",
                          placeContent: "center",
                          cursor: "pointer",
                          position: "relative",
                          display: "none"
                        }}
                      />
                      <label
                        htmlFor={value}
                        style={{
                          cursor: "pointer",
                          color: "black",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span
                          style={{
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            backgroundColor: modalPeriodType === value ? color : "transparent",
                            border: `2px solid ${color}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "0.3s",
                            pointer: "18px"
                          }}
                        >
                          {modalPeriodType === value && (
                            <span
                              style={{
                              }}
                            />
                          )}
                        </span>
                        {label}
                      </label>
                    </div>
                  ))}
                </div>

                <Typography
                  style={
                    {
                      color: "#152652"
                    }
                  }
                >
                  {(state.selectedStartDay && state.selectedEndDay) &&
                    `Période sélectionnée : ${format(state.selectedStartDay, "dd/MM/yyyy")} au ${format(state.selectedEndDay, "dd/MM/yyyy")}`}
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddPeriodFromModal}
                  style={{ marginTop: "20px" }}
                >
                  Confirmer
                </Button>
              </div>
            </Modal>}

            {from === "school" && <Modal
              onCancel={() => setDeleteModalOpen(false)}
              open={deleteModalOpen}
              title="Supprimer la période"
              withStep={false}
            >
              <Typography
                style={
                  {
                    color: "#152652"
                  }
                }
              >
                Êtes-vous sûr de vouloir supprimer la période en {" "}
                <span style={{ color: planningColorsText[selectedPeriodToDelete?.type] }}>{selectedPeriodToDelete?.type.toLowerCase()}</span> du{" "}
                {selectedPeriodToDelete && `${format(parseISO(selectedPeriodToDelete?.startDate), "dd/MM/yyyy")} au ${format(parseISO(selectedPeriodToDelete?.endDate), "dd/MM/yyyy")}`}
              </Typography>
              <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                <Button
                  variant="outlined"
                  onClick={() => setDeleteModalOpen(false)}
                  fullWidth
                >
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleDeletePeriod(selectedPeriodToDelete.id)}
                  style={{
                    borderRadius: "400px",
                  }}
                  fullWidth
                >
                  Supprimer
                </Button>
              </div>
            </Modal>}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = ({ spacing }) => ({
  leadContainer: {
    backgroundColor: "white",
    minHeight: 'calc(100vh - 75px - 93px)',
    borderRadius: '0 0 10px 10px',
    padding: "20px",
    height: '100%',
    width: '100%',
    "@media (max-width: 1048px)": {
      width: '100%',
    },
  },
  container: {
    position: "relative",
  },
  container2: {
    backgroundColor: '#F3F7FF',
    borderRadius: 10,
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "16px",
    paddingBottom: "20px",
    "@media (max-width:1048px)": {
      flexDirection: "column"
    },
  },
  buttonContainerLeft: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
  },
  buttonContainerRight: {
    display: "flex",
    gap: "16px",
    "@media (max-width:1048px)": {
      flexDirection: "column"
    },
  },
  circleRecapContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px"
  },
  circleRecapContainerDuo: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
  },
  circleLabel: {
    whiteSpace: "nowrap",
  },
  returnButton: {
    color: '#5A6DA1',
    '@media only screen and (max-width:1920px)': { fontSize: '13.500px' }, '@media only screen and (max-width:1600px)': { fontSize: '13.5000px' },
    border: '1px solid #C0D9FF',
    backgroundColor: 'white',
    borderRadius: '20px',
    width: '200px',
    fontWeight: 600,
    maxWidth: '200px',
    '&:hover': {
      backgroundColor: "white",
      border: "1px solid #0060F9",
      color: "#0060F9",
    },
    "@media (max-width:1048px)": {
      maxWidth: '100%',
    },
  },

  circle: {
    background: "red",
    borderRadius: "100%",
    width: "8px",
    height: "8px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  circleBorder: {
    position: "relative",
    border: "1px solid red",
    width: "16px",
    height: "16px",
    borderRadius: "400px",
  },
  hoursContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
    flexWrap: "wrap",
  },
});

export default PlanningManager;
